import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { TestContext } from 'yup';

import { WorkflowConfigFormValues } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/WorkflowConfigDialog/component';
import { WorkflowRoles } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/data';

export const useValidationSchema = () => {
  const { t } = useTranslation();

  const errorMessages = useMemo(
    () => ({
      roleNamesRequired: t('errorSelectionIsRequired'),
      roleNamesMax1: t('errorOnlySingleSelectedOptionAllowed'),
      roleNamesMin1: t('errorSelectionIsRequired'),
      initiator: {
        managerNotAlone: 'errorManagerNotAloneAsInitiator',
      },
      approvalSteps: {
        altManagerRequiresManager: t('errorAltManagerRequiresManager'),
        responsibleHROnlyOnLastStep: t('errorResponsibleHROnlyOnLastStep'),
        seqUndefined: t('errorApprovalStepConfigDataInvalid'),
        approvalStepRequired: t('errorApprovalStepRequired'),
      },
    }),
    [t],
  );

  return useMemo(() => {
    // Hacky workaround for yup checking empty roleNames array
    const testRoleNamesRequired = (
      value:
        | (object & { roleNames: string[]; seq: number })
        | (object & { roleNames: string[] })
        | null
        | undefined,
      context: TestContext,
    ) => {
      const path = !!context.path ? `${context.path}.roleNames` : 'roleNames';

      return value?.roleNames.length === 0
        ? context.createError({
            message: errorMessages.roleNamesRequired,
            path: path,
          })
        : true;
    };

    const yupApprovalStepRoleNames = yup
      .array()
      .of(yup.string().required())
      .required(errorMessages.roleNamesRequired)
      .min(1, errorMessages.roleNamesMin1)
      .test(
        'altManagerRequiresManager',
        errorMessages.approvalSteps.altManagerRequiresManager,
        (value) => {
          return !(
            !!value &&
            value.includes(WorkflowRoles.ALT_MANAGER) &&
            !value.includes(WorkflowRoles.MANAGER)
          );
        },
      );

    const yupApprovalStep = yup
      .object()
      .shape({
        roleNames: yupApprovalStepRoleNames,
        seq: yup.number().required(errorMessages.approvalSteps.seqUndefined),
      })
      .required()
      .test('roleNameRequired', 'Role name required', testRoleNamesRequired);

    const yupApprovalSteps = yup
      .array()
      .of(yupApprovalStep)
      .required()
      .test(
        'responsibleHROnlyOnLastStep',
        errorMessages.approvalSteps.responsibleHROnlyOnLastStep,
        (steps, context) => {
          const errorPaths =
            (!!steps &&
              steps
                .map((value, index) => {
                  const isLastStep = index === steps.length - 1;
                  const hasResponsibleHR = value?.roleNames.includes(
                    WorkflowRoles.RESPONSIBLE_HR,
                  );
                  const path = `${context.path}[${index}].roleNames`;

                  return !isLastStep && hasResponsibleHR ? path : false;
                })
                .filter((value) => !!value)) ||
            [];

          return errorPaths.length > 0
            ? context.createError({
                message:
                  errorMessages.approvalSteps.responsibleHROnlyOnLastStep,
                path: errorPaths[0] || context.path,
              })
            : true;
        },
      );

    const yupArrayOfStrings = yup.array().of(yup.string().required());

    const yupRequiredSingleRoleName = yup
      .object()
      .shape({
        roleNames: yupArrayOfStrings
          .required(errorMessages.roleNamesRequired)
          .min(1, errorMessages.roleNamesMin1)
          .max(1, errorMessages.roleNamesMax1),
      })
      .required()
      .test(
        'roleNameRequired',
        errorMessages.roleNamesRequired,
        (value, context) => testRoleNamesRequired(value, context),
      );

    const yupOptionalSingleRoleName = yup
      .object()
      .shape({
        roleNames: yupArrayOfStrings
          .defined()
          .max(1, errorMessages.roleNamesMax1),
      })
      .required();

    const yupRequiredRoleName = yup
      .object()
      .shape({
        roleNames: yupArrayOfStrings.defined(),
      })
      .required()
      .test('roleNameRequired', 'Role name required', testRoleNamesRequired);

    const yupInitiator = yupRequiredRoleName.test(
      'managerNotAlone',
      errorMessages.initiator.managerNotAlone,
      (value, context) => {
        if (!value?.roleNames) {
          return true;
        }
        const path = !!context.path ? `${context.path}.roleNames` : 'roleNames';

        const hasManager = value?.roleNames.includes(WorkflowRoles.MANAGER);

        return hasManager && value?.roleNames?.length > 1
          ? context.createError({
              message: errorMessages.initiator.managerNotAlone,
              path: path,
            })
          : true;
      },
    );

    return yup
      .object()
      .shape<WorkflowConfigFormValues>({
        initiators: yupInitiator,
        approvalSteps: yupApprovalSteps,
        closer: yupRequiredSingleRoleName,
        overseer: yupOptionalSingleRoleName,
        administrator: yupRequiredRoleName,
      })
      .required()
      .test(
        'approvalStepsRequired',
        errorMessages.approvalSteps.approvalStepRequired,
        (value, context) => {
          // Hacky workaround to test if array is empty
          const path = !!context.path
            ? `${context.path}.approvalSteps`
            : 'approvalSteps';

          return value?.approvalSteps.length === 0
            ? context.createError({
                message: errorMessages.approvalSteps.approvalStepRequired,
                path: path,
              })
            : true;
        },
      );
  }, [errorMessages]);
};
